import { useMemo } from 'react';

import useTranslations from '@/i18n/useTranslations';

import { BLOOM_DURATION, foldPresets } from '../constants';

const TAU = Math.PI * 2;

export default function KaleidoscopeStage({
  availableForDrawing,
  bloomCanvasRef,
  bloomClassName,
  bloomKey,
  containerRef,
  folds,
  isRevealed,
  remixPattern,
  setFolds,
  displayStartAngle,
  revealStartAngle,
  strokeBudgetSlots,
  usedBudget,
  wedgeCanvasRef,
}) {
  const t = useTranslations('common.newspaper.art_games.kaleidoscope.stage');
  const rotationDelta = useMemo(
    () => {
      let delta = revealStartAngle - displayStartAngle;
      while (delta <= -Math.PI) delta += TAU;
      while (delta > Math.PI) delta -= TAU;
      return delta;
    },
    [displayStartAngle, revealStartAngle],
  );
  const wedgeStyle = useMemo(() => {
    const wedgeAngle = TAU / folds;
    const normalizedStart = ((displayStartAngle % TAU) + TAU) % TAU;
    const endAngle = normalizedStart + wedgeAngle;
    const canvasSize = 100;

    const polarToPercent = (angle) => ({
      x: (50 + Math.cos(angle) * 50).toFixed(3),
      y: (50 + Math.sin(angle) * 50).toFixed(3),
    });

    const startPoint = polarToPercent(normalizedStart);
    const endPoint = polarToPercent(endAngle);
    const midAngle = normalizedStart + wedgeAngle / 2;
    const centroidDistance =
      wedgeAngle === 0 ? 0 : (4 * 50 * Math.sin(wedgeAngle / 2)) / (3 * wedgeAngle);
    const centroidX = 50 + Math.cos(midAngle) * centroidDistance;
    const centroidY = 50 + Math.sin(midAngle) * centroidDistance;

    const polygonPoints = [
      { x: 50, y: 50 },
      { x: Number(startPoint.x), y: Number(startPoint.y) },
    ];

    const arcSegments = Math.max(12, Math.ceil(wedgeAngle / (Math.PI / 24)));
    for (let index = 1; index < arcSegments; index += 1) {
      const angle = normalizedStart + (wedgeAngle * index) / arcSegments;
      polygonPoints.push({
        x: 50 + Math.cos(angle) * 50,
        y: 50 + Math.sin(angle) * 50,
      });
    }
    polygonPoints.push({ x: Number(endPoint.x), y: Number(endPoint.y) });

    const centeredPoints = polygonPoints.map((point) => ({
      x: point.x + (50 - centroidX),
      y: point.y + (50 - centroidY),
    }));

    const bounds = centeredPoints.reduce(
      (accumulator, point) => ({
        minX: Math.min(accumulator.minX, point.x),
        maxX: Math.max(accumulator.maxX, point.x),
        minY: Math.min(accumulator.minY, point.y),
        maxY: Math.max(accumulator.maxY, point.y),
      }),
      { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity },
    );

    const contentWidth = Math.max(bounds.maxX - bounds.minX, 1);
    const contentHeight = Math.max(bounds.maxY - bounds.minY, 1);
    const fitScale = Math.min(canvasSize / contentWidth, canvasSize / contentHeight);
    const focusScale = Math.max(1, fitScale * 0.82);

    return {
      // CSS custom properties feed the clip-path calculation so only the active wedge is visible pre-reveal.
      '--wedge-start-x': `${startPoint.x}%`,
      '--wedge-start-y': `${startPoint.y}%`,
      '--wedge-end-x': `${endPoint.x}%`,
      '--wedge-end-y': `${endPoint.y}%`,
      '--wedge-focus-offset-x': `${(50 - centroidX).toFixed(3)}%`,
      '--wedge-focus-offset-y': `${(50 - centroidY).toFixed(3)}%`,
      '--wedge-focus-scale': focusScale.toFixed(3),
      '--wedge-rotation': `${rotationDelta}rad`,
      '--bloom-duration': `${BLOOM_DURATION}ms`,
    };
  }, [displayStartAngle, folds, rotationDelta]);
  return (
    <section
      className={`kaleidoscope-stage ${isRevealed ? 'is-revealed' : ''}`}
      aria-label={t('aria.drawing_wedge')}
      style={wedgeStyle}
    >
      <div className="kaleidoscope-stage__stack">
        <div className="kaleidoscope-stage__rail kaleidoscope-stage__rail--top">
          <div className="kaleidoscope-budget" aria-label={t('aria.stroke_budget')}>
            {strokeBudgetSlots.map((slot) => (
              <span
                key={`stroke-slot-${slot}`}
                className={`kaleidoscope-budget__dot ${slot < usedBudget ? 'is-used' : ''}`}
              />
            ))}
            <span className="kaleidoscope-budget__label">{t('strokes_label')}</span>
          </div>
        </div>
        <div className="kaleidoscope-stage__canvas-wrapper" ref={containerRef}>
          <canvas
            ref={wedgeCanvasRef}
            className={`kaleidoscope-canvas ${availableForDrawing ? 'is-active' : 'is-locked'}`}
          />
          <canvas
            key={bloomKey}
            ref={bloomCanvasRef}
            className={bloomClassName}
          />
        </div>
        <div className="kaleidoscope-stage__rail kaleidoscope-stage__rail--bottom">
          <div className="kaleidoscope-folds" role="group" aria-label={t('aria.fold_presets')}>
            {foldPresets.map((preset) => (
              <button
                key={preset.id}
                type="button"
                className={`kaleidoscope-folds__button ${folds === preset.value ? 'is-active' : ''}`}
                onClick={() => (isRevealed ? remixPattern(preset.value) : setFolds(preset.value))}
                disabled={!isRevealed && preset.value === folds}
                aria-pressed={folds === preset.value}
              >
                {t(preset.labelKey)}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
